/**
 * Pure, testable helpers for the Design studio's block-editing affordances. Each
 * mutation returns a NEW {@link Issue} (never mutating the argument), so the studio
 * can update its optimistic in-memory issue synchronously and hand the same object
 * to the {@link IssueRenderer} for an instant re-render. The mutations mirror the
 * backend `patchDraft` semantics EXACTLY (dense 0-based renumber over kept blocks,
 * a blank title clearing the field) so the optimistic view matches the persisted
 * one and a later PATCH is a confirmation, never a correction.
 */
import type {
  ContentBlock,
  ContentBlockEdit,
  ContentBlockStatus,
  Issue,
  IssueEditInput,
} from '../../api';

/**
 * Renumber the KEPT blocks to a dense 0-based running order (sorted by their
 * current order, stable), leaving dismissed blocks' order untouched. This mirrors
 * the backend `renumberKept`, so a reorder or dismiss can never leave gaps or two
 * kept blocks sharing a position.
 */
export function renumberKept(blocks: readonly ContentBlock[]): ContentBlock[] {
  const denseOrder = new Map<string, number>();
  [...blocks]
    .filter((block) => block.status === 'kept')
    .sort((a, b) => a.order - b.order)
    .forEach((block, index) => denseOrder.set(block.id, index));
  return blocks.map((block) => {
    const next = denseOrder.get(block.id);
    return next !== undefined ? { ...block, order: next } : block;
  });
}

/**
 * The rail's blocks in curation order: the kept blocks first (in dense running
 * order), then the dismissed ones after (so a dismissed block can still be found
 * and restored, unlike the reader pane which drops them entirely).
 */
export function railBlocksInOrder(issue: Issue): ContentBlock[] {
  const byOrder = (a: ContentBlock, b: ContentBlock): number => a.order - b.order;
  const kept = issue.blocks
    .filter((block) => block.status !== 'dismissed')
    .slice()
    .sort(byOrder);
  const dismissed = issue.blocks
    .filter((block) => block.status === 'dismissed')
    .slice()
    .sort(byOrder);
  return [...kept, ...dismissed];
}

/** Toggle one block between kept and dismissed, then re-densify the kept order. */
export function toggleBlockStatus(issue: Issue, id: string): Issue {
  const blocks = issue.blocks.map((block): ContentBlock => {
    if (block.id !== id) return block;
    const status: ContentBlockStatus = block.status === 'kept' ? 'dismissed' : 'kept';
    return { ...block, status };
  });
  return { ...issue, blocks: renumberKept(blocks) };
}

/**
 * Move a KEPT block one position up (-1) or down (1) among the kept blocks by
 * swapping its order with its neighbour, then re-densify. A no-op (returns the
 * same issue) at the ends or for a block that is not kept.
 */
export function moveBlock(issue: Issue, id: string, direction: -1 | 1): Issue {
  const kept = issue.blocks
    .filter((block) => block.status === 'kept')
    .slice()
    .sort((a, b) => a.order - b.order);
  const index = kept.findIndex((block) => block.id === id);
  const swapWith = index + direction;
  if (index === -1 || swapWith < 0 || swapWith >= kept.length) {
    return issue;
  }
  const a = kept[index];
  const b = kept[swapWith];
  if (a === undefined || b === undefined) return issue;
  const blocks = issue.blocks.map((block): ContentBlock => {
    if (block.id === a.id) return { ...block, order: b.order };
    if (block.id === b.id) return { ...block, order: a.order };
    return block;
  });
  return { ...issue, blocks: renumberKept(blocks) };
}

/**
 * Set (or clear) a block's title, TRIMMED to mirror the backend `applyBlockEdit`
 * byte-for-byte: a blank-after-trim string CLEARS the title (drops the key) so the
 * block keeps the "title is absent or non-empty" invariant, and a padded title is
 * stored trimmed. Because the PATCH response is intentionally not reconciled into
 * the optimistic issue, the optimistic mirror must equal what the server persists,
 * or the two would silently drift. The BlockCard input holds its own untrimmed
 * draft while typing, so trimming here never fights the caret.
 */
export function setBlockTitle(issue: Issue, id: string, rawTitle: string): Issue {
  const trimmed = rawTitle.trim();
  return {
    ...issue,
    blocks: issue.blocks.map((block): ContentBlock => {
      if (block.id !== id) return block;
      const next: ContentBlock = { ...block };
      if (trimmed === '') {
        delete next.title;
      } else {
        next.title = trimmed;
      }
      return next;
    }),
  };
}

/** Set a block's prose body. */
export function setBlockBody(issue: Issue, id: string, body: string): Issue {
  return {
    ...issue,
    blocks: issue.blocks.map((block) => (block.id === id ? { ...block, body } : block)),
  };
}

/**
 * One block's full edit descriptor for a PATCH. A blank `title` is sent as `''`,
 * which the backend reads as "clear the title"; a block that never had a title is
 * a harmless no-op. Every field is sent so the PATCH is a full last-write-wins
 * snapshot that cannot race a partial patch.
 */
export function toBlockEdit(block: ContentBlock): ContentBlockEdit {
  return {
    id: block.id,
    // Trimmed to mirror the backend exactly (a blank-after-trim title clears it),
    // so a padded title the optimistic issue stored trimmed is sent identically.
    title: (block.title ?? '').trim(),
    body: block.body,
    order: block.order,
    status: block.status,
  };
}

/**
 * The full issue-edit body for a PATCH: the current subject, every block, and the
 * `expectedIssueId` the edit was built against. The server applies the patch only
 * while the stored draft still carries that issueId, so a late patch cannot clobber
 * a draft a regenerate replaced; a mismatch is a benign 409 the client ignores.
 */
export function toIssueEditInput(issue: Issue): IssueEditInput {
  return {
    subject: issue.subject,
    blocks: issue.blocks.map(toBlockEdit),
    expectedIssueId: issue.issueId,
  };
}
